import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { AuthUser } from '@app/account/account-api.model';
import { GroupIdentifier, GroupPrivacy } from '@app/groups/group-api';
import { OrgInfoService } from '@app/orgs/services/org-info.service';
import { AnyRecommendee } from '@app/recommendations/recommendations.model';
import { SelectOption } from '@app/shared/components/select/select.component';
import {
  ResourcePrivacyLevel,
  TargetType,
} from '@app/shared/models/core-api.model';
import { AuthService } from '@app/shared/services/auth.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { WindowToken } from '@app/shared/window.token';
import { SharedTargetService } from '@app/target/services/shared-target.service';
import {
  OrgTargetSettings,
  Target,
  TargetSettingsFormMode,
  TargetSettingsFormResult,
} from '@app/target/target-api.model';
import { InputImageUpload } from '@app/uploader/upload-section/adapters/input-image-upload.adapter';
import { UploaderService } from '@app/uploader/uploader.service';
import { UserSearchItem } from '@app/user/user-api.model';
import { DF_COLLAPSE_EXPAND } from '@lib/fresco';
import {
  DfFormFieldBuilder,
  DfFormFieldConfig,
  NotificationType,
} from '@lib/fresco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * @deprecated
 * @see {EditTargetComponent} instead.
 */
@Component({
  selector: 'dgx-target-settings-form',
  templateUrl: './target-settings-form.component.html',
  animations: [DF_COLLAPSE_EXPAND],
})
export class TargetSettingsFormComponent implements OnInit {
  @Input() public targetSource: Target;
  @Input() public loadTarget: boolean;
  @Input() public mode: TargetSettingsFormMode = 'new';
  @Input() public context: string;
  @Input() public autoPopulate: boolean;
  @Input() public showAutoPopulateOption: boolean;
  @Input() public navigateTo: boolean;
  @Input() public isAddToTarget: boolean = false;
  @Input() public isManagePlans: boolean = false;

  @ViewChild('planPurpose') public planPurpose: TemplateRef<any>;
  @ViewChild('suggestTitleInput')
  public suggestTitleInput: TemplateRef<any>;

  @ViewChild('subTitle')
  public subTitle: TemplateRef<any>;

  @ViewChild('description')
  public description: TemplateRef<any>;

  @ViewChild('imageUploader')
  public imageUploader: TemplateRef<any>;

  @ViewChild('type')
  public type: TemplateRef<any>;

  @ViewChild('primaryContact') public primaryContact: TemplateRef<any>;

  @ViewChild('visibility')
  public visibility: TemplateRef<any>;

  @ViewChild('autoPopulateToggleSwitch')
  public autoPopulateToggleSwitch: TemplateRef<any>;

  @ViewChild('endorsePlanToggleSwitch')
  public endorsePlanToggleSwitch: TemplateRef<any>;

  public readonly NotificationType = NotificationType;

  public target: Target;
  public groupIds: number[] = [];
  public groupNames: string[] = [];
  public isBrowseEditor: boolean;

  public isEditing: boolean;
  public isCreating: boolean;
  public isCloning: boolean;

  public limitOrgPublish: boolean;
  public uploadAdapter: InputImageUpload;
  public showImageUpload: boolean;
  public loading: boolean = true;
  public legacyPictureUrl: string;
  public headerText: string = '';
  public submitButtonText: string = '';
  public descriptionPlaceholderText: string;
  public privacyOptions: any;
  public targetTypeOptions: {
    id: TargetType;
    label: string;
    disabled: boolean;
  }[];
  public showTargetTypeOptions: boolean;
  public options: FormlyFormOptions = {};
  public isSubmitting: boolean = false;
  public orgId: number;
  public purposeSelect: SelectOption[] = [
    {
      id: 'Target' as TargetType,
      title: this.sharedTargetService.getTypeDisplayName('Target', false, true),
    },
    {
      id: 'Role' as TargetType,
      title: this.sharedTargetService.getTypeDisplayName('Role', false, true),
    },
  ];
  public form: FormGroup = new FormGroup({});
  public fields: DfFormFieldConfig[] = [];
  public model: {
    purpose?: TargetType;
    name: string;
    subtitle?: string;
    description?: string;
    imageUrl?: string;
    primaryContact?: UserSearchItem;
    visibility?: ResourcePrivacyLevel;
    type?: string;
    autoPopulate?: boolean;
    isEndorsed?: boolean;
  } = { name: '' };

  public i18n = this.translateService.instant([
    'Core_CloneOfFormat',
    'FlexCtrl_PrimaryContact',
    'Core_YesContinue',
    'Core_FieldRequired',
    'Core_Clone',
    'Core_Save',
    'OrgPlans_CustomTitlesAndDescriptionsNotLocalized',
    'PathwayPrivacyModal_OnlySetGroups',

    // Create option headers
    'OrgPlans_CreateTarget',
    'OrgPlans_CreateRole',
    'OrgPlans_CreateDirectory',

    // Clone option headers
    'OrgPlans_CloneTarget',
    'OrgPlans_CloneRole',
    'OrgPlans_CloneBrowse',
    'OrgPlans_CloneDirectory',

    // Edit option headers
    'OrgPlans_EditFeatured',
    'OrgPlans_EditDirectory',
    'OrgPlans_EditTarget',
    'OrgPlans_EditRole',

    // Form fields strings (in order of appearance)
    'OrgPlans_Purpose',
    'OrgPlans_Title',
    'OrgPlans_TitlePlaceholder',
    'OrgPlans_DirectoryTitlePlaceholder',
    'OrgPlans_Subtitle',
    'OrgPlans_SubtitlePlaceholder',
    'OrgPlans_SubtitleTooltip',
    'OrgPlans_Description',
    'OrgPlans_DirectoryDescriptionPlaceholder',
    'OrgPlans_TargetDescriptionPlaceholder',
    'OrgPlans_ImageUpload',
    'OrgPlans_Type',
    'FlexCtrl_PrimaryContact',
    'TargetForm_Visibility',
    'orgInternalContentVisibilitySelector_TargetGroupsLabel',
    'TargetForm_AutopopulateTitle',
    'TargetForm_AutopopulateInfo',
    'TargetForm_EndorsedTooltip',
    'TargetForm_IsEndorsedTitle',
    'TargetForm_IsEndorsedInfo',
  ]);

  private authUser: AuthUser;
  private originalTargetType: TargetType;

  constructor(
    private activeModal: NgbActiveModal,
    private authService: AuthService,
    private builder: DfFormFieldBuilder,
    private cdr: ChangeDetectorRef,
    private LDFlagService: LDFlagsService,
    private orgInfoService: OrgInfoService,
    private sharedTargetService: SharedTargetService,
    private targetsService: TargetsService,
    private translateService: TranslateService,
    private uploadService: UploaderService,
    @Inject(WindowToken) private windowRef: Window
  ) {}

  /**
   * Hide admin groups from plans
   */
  public get excludedGroupPrivacyLevels(): GroupPrivacy[] {
    return this.target.targetType === 'Target' ? ['Administrative'] : [];
  }

  public get showVisibilityOptions(): boolean {
    return (
      !(this.context === 'channel') || !(this.target.targetType === 'Browse')
    );
  }

  /** Returns true if the plan purpose should display. */
  public get showPlanPurposeOption(): boolean {
    return !(this.isDirectory || this.isBrowse);
  }

  /** Return true if we should pre-populate the dropdown. Otherwise force the user to pick between skill/role plan. */
  public get showSelectedPurpose(): boolean {
    return (
      this.isCreating && !this.isCloning && !this.isDirectory && !this.isBrowse
    );
  }

  /** Returns true if the current target type is 'Directory' */
  public get isDirectory(): boolean {
    return this.target?.targetType === 'Directory';
  }

  /** Returns true if the current target type is 'Browse'
   * @Note This is an old alias name for the new 'Featured' plan
   */
  public get isBrowse(): boolean {
    return this.target?.targetType === 'Browse';
  }

  public ngOnInit(): void {
    this.isEditing = this.mode === 'edit';
    this.isCreating = this.mode === 'new' || this.mode === 'clone';
    this.isCloning = this.mode === 'clone';

    this.authUser = this.authService.authUser;
    this.isBrowseEditor = this.authUser.canConfigureBrowse;

    this.limitOrgPublish = true;
    this.uploadAdapter = new InputImageUpload(
      this.uploadService,
      'Target',
      this.isCreating ? null : this.targetSource?.targetId
    );
    const channelContext = this.context === 'channel';
    this.orgId =
      this.targetSource?.organizationId || this.authUser.defaultOrgId;
    this.initTargetInformation(channelContext);
  }

  /**
   * Update the description text on changes. Necessary to work around the ngDefaultControl
   * on MarkdownEditorComponent.
   * @param desc - Description text to update value.
   * @param formControl - Form control to update.
   */
  public onDescriptionChange(desc: string, formControl: AbstractControl): void {
    formControl.setValue(desc);
  }

  /**
   * On plan selection dropdown update the title name
   * @param name
   * @param formControl
   */
  public onItemsFieldChange(name: string, formControl: FormControl): void {
    this.target = { ...this.target, name: name ?? '' };
    formControl.setValue(name);
    formControl.markAsTouched();
  }

  public onImageUploadSuccess({
    pictureUrl,
    legacyPictureUrl,
    resourceImageId,
  }): void {
    if (pictureUrl) {
      this.target.image = pictureUrl;
      this.legacyPictureUrl = legacyPictureUrl;
      this.target.resourceImageId = resourceImageId;
    }
  }

  public onDeleteImage() {
    this.target.image = undefined;
    this.legacyPictureUrl = undefined;
    this.target.resourceImageId = '';
  }

  public handleToggle(toggleValue: boolean, setting: string): void {
    switch (setting) {
      case 'autoPopulate':
        this.autoPopulate = toggleValue;
        break;
      case 'isEndorsed':
        this.target.isEndorsed = toggleValue;
        break;
    }
  }

  public submit(): void {
    this.isSubmitting = true;
    this.form.markAllAsTouched();
    this.cdr.detectChanges();

    if (this.form.invalid) {
      this.isSubmitting = false;
      // rebuild fields  Note on submit error was not displaying
      this.fields = this.buildFormFields();
      this.form.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }
    this.save();
  }

  public handleGroupsAdd = ({ model }) => {
    if (model.groupId && this.groupIds.indexOf(model.groupId) === -1) {
      this.groupIds.push(model.groupId);
    }
    if (this.groupNames.indexOf(model.name) === -1) {
      this.groupNames.push(model.name);
    }
    this.onGroupChange();
  };

  public handleGroupsRemove = ({ group }) => {
    this.groupNames.filter((name) => name !== group.name);
    if (group.groupId) {
      this.groupIds.filter((id) => id !== group.groupId);
    }
    this.onGroupChange();
  };

  public onGroupChange(): void {
    const groupIdsObj = [];
    for (let i = 0; i < this.groupIds.length; i++) {
      groupIdsObj.push({
        id: this.groupIds[i],
        name: this.groupNames[i],
        isRestricted: this.target.privacyId === 2,
      });
    }
    this.target.groupIds = groupIdsObj;
    this.cdr.detectChanges();
  }

  public onPrimaryContactSelect = (searchItem: AnyRecommendee) => {
    const user = searchItem as UserSearchItem;
    this.target.primaryContact = user;
    this.target.primaryContactUserKey = user.userProfileKey;
  };

  public onPrimaryContactRemove(): void {
    this.target.primaryContact = undefined;
    this.target.primaryContactUserKey = undefined;
  }

  public onPurposeTypeChange(
    purpose: SelectOption,
    formControl: FormControl
  ): void {
    this.target.targetType = purpose.id;
    this.setTargetModalString(purpose.id);
    this.model.purpose = purpose.id;
    formControl.setValue(this.model.purpose);
    formControl.markAsTouched();
  }

  public onTypeChange(type: TargetType): void {
    // Set the new type, except if the type is Role.. then display this as Target = "Plan".
    // This allows changing the Target Purpose between Skill and Role Plan while they are actual separate types.
    this.model.type = type === 'Role' ? 'Target' : type;
    this.target.targetType = type;

    // Update i18n strings and privacy options for the type selected
    this.setTargetModalString(type);
    this.initPrivacyOptions();
  }

  public getHiddenWhenCondition(field: string): boolean {
    switch (field) {
      case 'purpose':
        return this.isDirectory || this.isBrowse;
      case 'title':
        // should never be hidden
        return false;
      case 'subtitle':
        return this.isBrowse;
      case 'description':
        // should never be hidden
        return false;
      case 'imageUploader':
        return !this.showImageUpload;
      case 'type':
        return !this.showTargetTypeOptions;
      case 'primaryContact':
        // should never be hidden?
        return false;
      case 'visibility':
        return !this.showVisibilityOptions;
      case 'autoPopulate':
        return (
          this.isDirectory || this.isEditing || !this.showAutoPopulateOption
        );
      case 'endorsed':
        return (
          this.isBrowse || this.isDirectory || !this.authUser.canEndorseContent
        );
    }
  }

  /**
   * On save of from
   */
  private save(): void {
    if (
      this.target.targetType === 'Browse' ||
      this.originalTargetType === 'Browse'
    ) {
      this.targetsService.clearBrowseTargetCache();
    }
    if (this.legacyPictureUrl) {
      this.target.image = this.legacyPictureUrl;
    }
    if (this.isCloning) {
      this.targetsService
        .cloneTarget(this.target)
        .subscribe((response) => this.onSaved(response));
    } else if (this.isEditing) {
      this.targetsService
        .updateTarget(this.target)
        .subscribe((response) => this.onSaved(response));
    } else {
      this.targetsService
        .addTarget(this.target)
        .pipe(
          map((response: number) => {
            this.target = { ...this.target, targetId: response };
            return response;
          })
        )
        .subscribe((response) => {
          this.onSaved(response);
        });
    }
  }

  /**
   * On Form saved
   */
  private onSaved(response: number): void {
    this.target.referenceId = response;
    this.isSubmitting = false;

    const result: TargetSettingsFormResult = {
      target: this.target,
      autoPopulate: this.autoPopulate,
    };

    this.closeActiveModal(result);

    // TODO: Replace this with angular router
    // routes to newly created plan
    if (
      (this.isCreating && !this.isAddToTarget) ||
      (this.navigateTo && result.target)
    ) {
      const navArgs = {
        target: this.target,
        context: '',
        autoPopulate: this.autoPopulate,
      };
      this.sharedTargetService.navigateToTarget(navArgs);
    } else if (this.isEditing && !this.isManagePlans) {
      // the page is not refreshed like it used to when an edit is made
      // refresh the page so that the plan info is updated.
      this.windowRef.location.reload();
    }
  }

  /**
   * Close the active modal and send the target settings from result back.
   */
  private closeActiveModal(result: TargetSettingsFormResult) {
    return this.activeModal.close(result);
  }

  private initFormModel(): void {
    this.model.name = this.target.name;
    this.model.subtitle = this.target.subtitle;
    this.model.description = this.target.description;
    this.model.imageUrl = this.target.imageUrl;
    this.model.type =
      this.target.targetType === 'Role' ? 'Target' : this.target.targetType;
    this.model.visibility = this.target.privacyId;
    this.model.isEndorsed = this.target.isEndorsed;

    // Model init
    this.model.primaryContact = this.target.primaryContact;

    // only set the model with the selected purpose if we are editing
    // and if the targetType is a Target or Role
    if (
      this.isEditing &&
      (this.target.targetType === 'Target' || this.target.targetType === 'Role')
    ) {
      this.model.purpose = this.target.targetType;
    }
  }

  private buildFormFields(): DfFormFieldConfig[] {
    const fields: DfFormFieldConfig[] = [];

    // Title
    fields.push(
      this.builder
        .customField('name', this.i18n.OrgPlans_Title, this.suggestTitleInput, {
          defaultValue: this.target.name,
          autofocus: true,
        })
        .hiddenWhen(() => this.getHiddenWhenCondition('title'))
        .asRequired()
        .updatedOn('submit')
        .build()
    );

    // Subtitle
    fields.push(
      this.builder
        .customField('subtitle', null, this.subTitle)
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('subtitle'))
        .build()
    );

    // Plan purpose
    fields.push(
      this.builder
        .customField('purpose', this.i18n.OrgPlans_Purpose, this.planPurpose, {
          required: true,
        })
        .asRequired()
        .hiddenWhen(() => this.getHiddenWhenCondition('purpose'))
        .build()
    );

    // description
    fields.push(
      this.builder
        .customField(
          'description',
          this.i18n.OrgPlans_Description,
          this.description,
          {
            required: false,
          }
        )
        .asOptional()
        .hiddenWhen(() => this.getHiddenWhenCondition('description'))
        .build()
    );

    // image uploader
    fields.push(
      this.builder
        .customField('', null, this.imageUploader)
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('imageUploader'))
        .build()
    );

    // type
    fields.push(
      this.builder
        .customField('type', null, this.type)
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('type'))
        .build()
    );

    // primary contact
    fields.push(
      this.builder
        .customField('primaryContact', null, this.primaryContact)
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('primaryContact'))
        .build()
    );

    // visibility
    fields.push(
      this.builder
        .customField('visibility', null, this.visibility)
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('visibility'))
        .build()
    );

    // autoPopulate
    fields.push(
      this.builder
        .customField(
          'autoPopulateToggleSwitch',
          null,
          this.autoPopulateToggleSwitch
        )
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('autoPopulate'))
        .build()
    );

    // endorsePlan
    fields.push(
      this.builder
        .customField(
          'endorsePlanToggleSwitch',
          null,
          this.endorsePlanToggleSwitch
        )
        .unwrapped()
        .hiddenWhen(() => this.getHiddenWhenCondition('endorsed'))
        .build()
    );

    return fields;
  }

  /**
   * Initiate target information for form.
   * @param channelContext boolean
   */
  private initTargetInformation(channelContext: boolean): void {
    let resolveTarget: Observable<Target>;
    if (this.isCreating) {
      const newTarget: Target = {
        name: '',
        subtitle: '',
        description: '',
        privacyId: channelContext ? 1 : 0, // channel must default to "Visible to Org"
        groupIds: [],
        targetType: this.isDirectory ? 'Directory' : 'Target',
        type: 'JobRole', // title suggestion type
        image: '', // db field for image url
        imageUrl: '', // for display, formatted by the server
        isEndorsed: false, // Endorsed content should be false by default when creating a plan [PD-81341].
        ...this.targetSource, // Copy any defaults passed in. We expect a Target here, not a promise, which wouldn't make much sense in this context
      };
      resolveTarget = of(newTarget);
    } else {
      if (!this.targetSource) {
        throw new Error(
          'Target data must be provided when editing an existing target'
        );
      }

      if (this.loadTarget) {
        // It's possible the target info provided is just partial, or not in-sync with the loaded target.
        // In this case force-load the target first.
        resolveTarget = this.targetsService
          .getTarget(this.targetSource.targetId)
          .pipe(
            map((response) => {
              return response.target;
            })
          );
      } else {
        resolveTarget = of(this.targetSource);
      }
    }

    resolveTarget.subscribe((target: Target) => {
      this.target = target;
      this.originalTargetType = this.target.targetType;
      this.showTargetTypeOptions =
        this.isEditing &&
        this.authUser.canManageTargets &&
        this.authUser.hasCareerPathing;

      if (this.isCloning) {
        this.target.name = this.translateService.instant('Core_CloneOfFormat', {
          title: this.target.name,
        });
        // Cloned Plans should set isEndorsed to false. Keep this here to ensure original plan isEndorsed flag is overriden [PD-81849]
        this.target.isEndorsed = false;
      }

      this.setupPrivacyGroups(this.target.groupIds);

      // After sorting out the state of the target to load
      // get the cached target settings to determine imageUploader state (see comment below)
      // and then finish up init of remaining modal settings
      this.orgInfoService
        .getTargetSettings(this.orgId || target.organizationId)
        .subscribe((targetSettings: OrgTargetSettings) => {
          // Show the image uploader for:
          // Any Target beside Browse/Featured type AND
          // force display even if the target is a Directory *if*
          // the org (The Bank only, see PD-36996 and PD-PD-42313 for more info) has
          // the ops tool setting 'DisablePlanImage' enabled
          this.showImageUpload =
            !this.isBrowse &&
            (this.isDirectory || !targetSettings.disablePlanImage);

          this.setTargetModalString(target.targetType);
          this.initTargetTypeOptions();
          this.initPrivacyOptions();
          this.initFormModel();
          this.fields = this.buildFormFields();

          this.loading = false;
        });
    });
  }

  private initTargetTypeOptions(): void {
    // under certain cases we want to show the types but in a disabled
    // state if the user does not have permission to edit the featured plan
    const disableAll = this.isBrowse && !this.isBrowseEditor;

    this.targetTypeOptions = [
      {
        id: 'Target',
        label: this.translateService.instant('OrgPlans_Plan'),
        disabled: disableAll,
      },
      {
        id: 'Directory',
        label: this.translateService.instant('OrgPlans_Directory'),
        disabled: disableAll,
      },
    ];

    if (this.isBrowseEditor || this.isBrowse) {
      this.targetTypeOptions.push({
        id: 'Browse',
        label: this.translateService.instant('OrgPlans_Featured'),
        disabled: disableAll,
      });
    }
  }

  private initPrivacyOptions(): void {
    const privacyOptions = this.targetsService.getPrivacyOptions(
      this.authUser,
      this.isCreating,
      this.groupIds,
      this.target
    );

    this.privacyOptions = privacyOptions.optionList;

    this.limitOrgPublish = privacyOptions.permissions.limitOrgPublish;
    if (this.isCloning) {
      this.target.privacyId = 0;
    } else if (
      !this.privacyOptions.find((option) => option.id === this.target.privacyId)
        ?.enabled
    ) {
      this.target.privacyId = 1;
    }
    this.togglePanels(this.privacyOptions, this.target.privacyId, true);
  }

  private togglePanels(options, privacyId, preventClear): void {
    this.setOpenStatus(options, privacyId);
    if (!preventClear) {
      this.clearGroups();
    }
    this.target.privacyId = privacyId;
  }

  private clearGroups(): void {
    if (!this.limitOrgPublish) {
      this.groupIds = [];
      this.groupNames = [];
    }
  }

  private setOpenStatus(options, privacyId): void {
    for (const i of options) {
      i.open = i.Id === privacyId;
      if (i.Id === privacyId) {
        i.open = true;
      }
    }
  }

  private setupPrivacyGroups(gIds: GroupIdentifier[]): void {
    if (this.target.privacyId === 0 || this.target.privacyId === 3) {
      this.groupIds = [];
      this.groupNames = [];
    } else {
      if (!this.groupIds) {
        this.groupIds = [];
      }
      if (!this.groupNames) {
        this.groupNames = [];
      }
      for (const i of gIds) {
        this.groupNames.push(i.name);
        this.groupIds.push(i.groupId);
      }
    }
  }

  /**
   * Sets or updates the modals i18n strings for title, description,
   * description placeholder and submit button depending on type and state.
   *
   * @param targetType
   */
  private setTargetModalString(targetType: TargetType): void {
    let headerText: string;
    let submitButtonText: string;
    let descriptionPlaceholderText: string;

    // Note: We're checking isCloning before isCreated because
    // isCreated also returns true if we are cloning.
    switch (targetType) {
      case 'Target':
        if (this.isCloning) {
          headerText = this.i18n.OrgPlans_CloneTarget;
          submitButtonText = this.i18n.Core_Clone;
        } else if (this.isCreating) {
          headerText = this.i18n.OrgPlans_CreateTarget;
          submitButtonText = headerText;
          // else, if we are editing
        } else {
          headerText = this.i18n.OrgPlans_EditTarget;
          submitButtonText = this.i18n.Core_Save;
        }
        descriptionPlaceholderText =
          this.i18n.OrgPlans_TargetDescriptionPlaceholder;
        break;
      case 'Role':
        if (this.isCloning) {
          headerText = this.i18n.OrgPlans_CloneRole;
          submitButtonText = this.i18n.Core_Clone;
        } else if (this.isCreating) {
          headerText = this.i18n.OrgPlans_CreateRole;
          submitButtonText = headerText;
          // else, if we are editing
        } else {
          headerText = this.i18n.OrgPlans_EditRole;
          submitButtonText = this.i18n.Core_Save;
        }
        descriptionPlaceholderText =
          this.i18n.OrgPlans_TargetDescriptionPlaceholder;
        break;
      case 'Directory':
        if (this.isCloning) {
          headerText = this.i18n.OrgPlans_CloneDirectory;
          submitButtonText = this.i18n.Core_Clone;
        } else if (this.isCreating) {
          headerText = this.i18n.OrgPlans_CreateDirectory;
          submitButtonText = headerText;
          // else, if we are editing
        } else {
          headerText = this.i18n.OrgPlans_EditDirectory;
          submitButtonText = this.i18n.Core_Save;
        }
        descriptionPlaceholderText =
          this.i18n.OrgPlans_DirectoryDescriptionPlaceholder;
        break;
      case 'Featured':
      case 'Browse':
        // Browse is a deprecated type that has been semi-replaced with 'Featured'
        if (this.isCloning || this.isCreating) {
          console.warn('The featured plan should not be able to be cloned!');
          // else, if we are editing
        } else {
          headerText = this.i18n.OrgPlans_EditFeatured;
          submitButtonText = this.i18n.Core_Save;
        }
        descriptionPlaceholderText =
          this.i18n.OrgPlans_TargetDescriptionPlaceholder;
        break;
    }

    this.headerText = headerText;
    this.submitButtonText = submitButtonText;
    this.descriptionPlaceholderText = descriptionPlaceholderText;
  }
}
